import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { Formik, FormikHelpers } from 'formik';

import type { PromoValues } from 'app/api/services/promo/types';
import { ResponseValueType } from 'app/api/services/promo/types';

import { API, ResponseStatus } from 'app/api';
import { useTranslation } from 'app/lang';
import { getFreebets } from 'app/lib/account/actions';
import { hasAccessToPromoCodeActivation } from 'app/lib/account/selectors';
import { closeSigningDialog } from 'app/modules/SigningNew/actions';
import { AuthModalQuery, createAuthQuery } from 'app/modules/SigningNew/utils/auth-modals-query';

import { createSigningQuery, SigningModalsQuery } from '../../modules/Signing/utils/signing-modals-query';
import Content from './Content';
import { getErrorMessage } from './error-message-utils';

import dictionary from './PromocodeForm.dictionary.json';

const INITIAL_VALUES = { code: '' };
const GET_FREEBETS_TIMEOUT = 30;
const CLOSE_SIGNING_DIALOG_TIMEOUT = 2000;

const PromocodeForm = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const hasAccessToPromoCode = useSelector(hasAccessToPromoCodeActivation);
  const { search } = useLocation();
  const isRegistration = search.startsWith(createSigningQuery(SigningModalsQuery.REGISTRATION));
  const isAuthentication = search.startsWith(
    createAuthQuery(AuthModalQuery.SIGN),
  );

  const timeout = useRef<number | null>(null);
  const [charError, setCharError] = useState<ReactNode>('');

  const getSuccessStatus = (type: ResponseValueType) => ({
    ['response-status']: ResponseStatus.SUCCESS,
    message: type === ResponseValueType.DEPOSIT
      ? t(dictionary.acceptedDeposit)
      : t(dictionary.acceptedFreebet, { count: GET_FREEBETS_TIMEOUT }),
  });

  const promoSubmit = useCallback((
    data : PromoValues,
    actions: FormikHelpers<PromoValues>,
  ) => {
    if (!hasAccessToPromoCode) {
      setCharError(t(dictionary.accessIsRestricted));
      actions.setSubmitting(false);

      return;
    }

    setCharError('');
    if (!data.code.length) {
      actions.setErrors({ code: t(dictionary.enterPromo) });
      actions.setSubmitting(false);
      return;
    }

    API.promo.checkPromo(data)
      .then((response) => {
        if (response.status !== ResponseStatus.SUCCESS) {
          // TODO: https://jira.tennisi.it/browse/SITE-742
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const responseData = response?.data as any;

          actions.setStatus(response.status);
          actions.setErrors({ code: getErrorMessage(responseData?.type, t) });
          actions.setSubmitting(false);
          return;
        }

        actions.setStatus(getSuccessStatus(response.data.type));
        if (isRegistration || isAuthentication) {
          timeout.current = window.setTimeout(
            () => dispatch(closeSigningDialog()),
            CLOSE_SIGNING_DIALOG_TIMEOUT,
          );
        }
        else {
          if (response.data.type === ResponseValueType.FREEBET) {
            timeout.current = window.setTimeout(
              () => dispatch(getFreebets()),
              GET_FREEBETS_TIMEOUT * 1000,
            );
          }
        }
      });
  }, []);

  useEffect(() => {
    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, []);

  return (
    <Formik onSubmit={promoSubmit}
            initialValues={INITIAL_VALUES}>
      <Content charError={charError}
               setCharError={setCharError}
               isRegistration={isRegistration || isAuthentication} />
    </Formik >
  );
};

export default PromocodeForm;
